import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Search, ArrowRight, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, dateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill, EmptyState, Field } from "@/components/Bits";
import ConvertInquiry from "@/components/ConvertInquiry";

const TONE = {
  new: "bg-[#BEC221] text-ink",
  contacted: "bg-[#EDEFC4] text-ink",
  consultation_scheduled: "bg-[#EDEFC4] text-ink",
  proposal_sent: "bg-[#EDEFC4] text-ink",
  converted: "bg-ink text-white",
  not_moving_forward: "bg-line text-stone",
};

export default function Inquiries() {
  const { isStaff, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ statuses: [] });
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [converting, setConverting] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    setError("");
    api
      .get("/inquiries", { params: { status: status || undefined, q: q || undefined } })
      .then((r) => setData(r.data))
      .catch((e) => {
        setData({ items: [] });
        setError(apiError(e));
      });
  };

  useEffect(() => {
    api.get("/inquiries/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  // Deep link from the dashboard: /inquiries?open=<id>
  useEffect(() => {
    const id = params.get("open");
    if (!id) return;
    openDetail(id);
    setParams({}, { replace: true });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [status, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (inquiry, value) => {
    try {
      await api.patch(`/inquiries/${inquiry.id}/status`, { status: value });
      toast.success("Inquiry updated");
      load();
      if (open?.id === inquiry.id) openDetail(inquiry.id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openDetail = (id) =>
    api
      .get(`/inquiries/${id}`)
      .then((r) => {
        setOpen(r.data);
        return r.data;
      })
      .catch((e) => {
        toast.error(apiError(e));
        return null;
      });

  if (user && !isStaff) return <Navigate to="/" replace />;

  return (
    <div className="space-y-10" data-testid="inquiries-page">
      <PageHeader
        overline="Website"
        title="Inquiries"
        subtitle="Everything that comes in through asthetik.design, from first hello to signed project."
      />

      {error && (
        <div data-testid="inquiries-error" className="flex items-start gap-3 rounded-sm border border-[#E07A5F] bg-[#F6DED6] px-5 py-4 text-sm text-[#A8492E]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" strokeWidth={1.5} />
          <input
            data-testid="inquiries-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, company or idea"
            className="w-full rounded-sm border border-line bg-white py-3 pl-11 pr-4 text-sm outline-none transition-colors focus:border-lime"
          />
        </div>
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          <button
            data-testid="inquiry-filter-all"
            onClick={() => setStatus("")}
            className={`shrink-0 rounded-sm border px-4 py-2.5 text-xs uppercase tracking-[0.12em] transition-colors ${!status ? "border-ink bg-ink text-white" : "border-line bg-white text-stone hover:text-ink"}`}
          >
            All
          </button>
          {meta.statuses.map((s) => (
            <button
              key={s.value}
              data-testid={`inquiry-filter-${s.value}`}
              onClick={() => setStatus(s.value)}
              className={`shrink-0 rounded-sm border px-4 py-2.5 text-xs uppercase tracking-[0.12em] transition-colors ${status === s.value ? "border-ink bg-ink text-white" : "border-line bg-white text-stone hover:text-ink"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="py-16 text-center text-sm text-fog">Loading inquiries…</div>
      ) : data.items.length === 0 ? (
        <EmptyState testId="inquiries-empty" title="No inquiries here yet"
          body="New submissions from the website appear here the moment they arrive." />
      ) : (
        <div className="card-surface divide-y divide-line" data-testid="inquiries-list">
          {data.items.map((i) => (
            <div key={i.id} data-testid={`inquiry-row-${i.id}`}
              className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center">
              <button onClick={() => openDetail(i.id)}
                data-testid={`inquiry-open-${i.id}`}
                className="min-w-0 flex-1 text-left">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-serif text-lg text-ink">{i.name}</span>
                  <Pill tone={TONE[i.status]} testId={`inquiry-status-${i.id}`}>{i.status_label}</Pill>
                  {i.link_broken && (
                    <Pill tone="bg-[#F6DED6] text-[#A8492E]" testId={`inquiry-broken-${i.id}`}>Link needs attention</Pill>
                  )}
                </div>
                <div className="mt-1.5 text-xs text-fog [overflow-wrap:anywhere]">{i.email}</div>
                {i.project_type && <div className="mt-2 text-sm text-stone">{i.project_type}</div>}
              </button>
              <div className="flex shrink-0 flex-wrap items-center gap-3">
                <span className="num text-xs text-fog">{dateShort(i.created_at)}</span>
                {i.local_project ? (
                  <Link
                    data-testid={`inquiry-project-link-${i.id}`}
                    to={`/projects/${i.local_project.id}`}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-lime-dark hover:underline"
                  >
                    {i.local_project.name} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Link>
                ) : (
                  <button
                    data-testid={`inquiry-convert-${i.id}`}
                    onClick={() => openDetail(i.id).then((r) => r && setConverting(i.id))}
                    className="rounded-sm bg-lime px-4 py-2.5 text-xs font-medium uppercase tracking-[0.12em] text-ink transition-colors hover:bg-lime-dark"
                  >
                    Convert to Project
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && !converting && (
        <InquiryDetail
          inquiry={open}
          statuses={meta.statuses}
          onClose={() => setOpen(null)}
          onStatus={(v) => changeStatus(open, v)}
          onConvert={() => setConverting(open.id)}
        />
      )}

      {open && converting === open.id && (
        <ConvertInquiry
          inquiry={open}
          onClose={() => {
            setConverting(null);
            setOpen(null);
          }}
          onDone={() => {
            setConverting(null);
            setOpen(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function InquiryDetail({ inquiry, statuses, onClose, onStatus, onConvert }) {
  const labelFor = (value) =>
    statuses.find((s) => s.value === value)?.label || value;
  const rows = [
    ["Email", inquiry.email],
    ["Phone", inquiry.phone],
    ["Company", inquiry.company],
    ["What they're making", inquiry.project_type],
    ["Location", inquiry.location],
    ["Estimated budget", inquiry.budget],
    ["Project date", inquiry.deadline],
    ["Follow-up date", inquiry.follow_up_date],
    ["Submitted", dateShort(inquiry.created_at)],
  ].filter(([, v]) => v);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex justify-end bg-ink/40 backdrop-blur-sm" onClick={onClose}
      data-testid="inquiry-detail">
      <div onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-xl overflow-y-auto bg-bone p-7 sm:p-10">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="overline">Inquiry</div>
            <h2 className="mt-2 font-serif text-3xl tracking-tight text-ink">{inquiry.name}</h2>
          </div>
          <button data-testid="inquiry-detail-close" onClick={onClose}
            className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">Close</button>
        </div>

        <div className="mt-8 space-y-4">
          <Field label="Pipeline status">
            <select
              data-testid="inquiry-status-select"
              value={inquiry.status}
              disabled={inquiry.status === "converted"}
              onChange={(e) => onStatus(e.target.value)}
              className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime disabled:opacity-60"
            >
              {statuses.map((s) => (
                <option key={s.value} value={s.value} disabled={!s.settable}>{s.label}</option>
              ))}
            </select>
          </Field>
          {inquiry.status === "converted" && inquiry.local_project && (
            <Link to={`/projects/${inquiry.local_project.id}`}
              data-testid="inquiry-detail-project-link"
              className="inline-flex items-center gap-2 text-sm text-lime-dark hover:underline">
              Open {inquiry.local_project.name} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          )}
        </div>

        {inquiry.idea && (
          <div className="mt-8">
            <div className="overline mb-2">Their idea</div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">{inquiry.idea}</p>
          </div>
        )}

        <dl className="mt-8 divide-y divide-line border-t border-line">
          {rows.map(([label, value]) => (
            <div key={label} className="flex flex-wrap gap-2 py-3.5">
              <dt className="w-48 shrink-0 text-xs uppercase tracking-[0.12em] text-fog">{label}</dt>
              <dd className="min-w-0 flex-1 text-sm text-ink [overflow-wrap:anywhere]">{value}</dd>
            </div>
          ))}
        </dl>

        {inquiry.files?.length > 0 && (
          <div className="mt-8">
            <div className="overline mb-3">Attachments</div>
            <div className="space-y-2">
              {inquiry.files.map((f) => (
                <a key={f.storage_path} href={f.url} target="_blank" rel="noreferrer"
                  data-testid="inquiry-file-link"
                  className="flex items-center gap-2 break-all text-sm text-lime-dark hover:underline">
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                  {f.original_filename}
                </a>
              ))}
            </div>
          </div>
        )}

        {inquiry.messages?.length > 0 && (
          <div className="mt-8" data-testid="inquiry-messages">
            <div className="overline mb-3">Emails sent from the website admin</div>
            <div className="space-y-3">
              {inquiry.messages.filter((m) => m.direction === "out").map((m) => (
                <div key={m.id} className="rounded-sm border border-line bg-white px-4 py-3">
                  <div className="num text-xs text-fog">
                    {dateShort(m.sent_at)} · {m.sent_by || "Website admin"}
                  </div>
                  {m.subject && <div className="mt-1 font-serif text-base text-ink">{m.subject}</div>}
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-stone">{m.body}</p>
                  {m.attachments?.length > 0 && (
                    <div className="mt-2 text-xs text-fog">
                      {m.attachments.map((a) => a.original_filename).join(" · ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs leading-relaxed text-fog">
              Outbound only. Client replies go to hello@asthetik.design and are not synced here.
            </p>
          </div>
        )}

        {inquiry.notes?.length > 0 && (
          <div className="mt-8" data-testid="inquiry-notes">
            <div className="overline mb-3">Internal notes from the website</div>
            <div className="space-y-3">
              {inquiry.notes.map((n) => (
                <div key={n.id} className="rounded-sm border border-dashed border-line px-4 py-3">
                  <div className="num text-xs text-fog">{dateShort(n.created_at)} · {n.created_by}</div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{n.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {inquiry.status_history?.length > 0 && (
          <div className="mt-8">
            <div className="overline mb-3">History</div>
            <div className="space-y-2 text-xs text-fog">
              {inquiry.status_history.map((h, idx) => (
                <div key={idx} className="num">
                  {dateShort(h.changed_at)} · {labelFor(h.status)} · {h.changed_by}
                </div>
              ))}
            </div>
          </div>
        )}

        {!inquiry.local_project && inquiry.status !== "converted" && (
          <button
            data-testid="inquiry-detail-convert-btn"
            onClick={onConvert}
            className="mt-10 w-full rounded-sm bg-lime px-6 py-3.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark"
          >
            Convert to Project
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}

export {};
